import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Trash2, Maximize2, Minimize2, CalendarX, MessageCircle, Send, ChevronDown, Copy, Plus, Columns2, ExternalLink, Link2, BarChart3, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { STATUSES, FORMATS, OBJECTIFS, METRICS, fmtDay, engagement, fmtPct } from "./contentUtils";
import { RichTextField } from "../RichTextField";
import { htmlToText } from "../../lib/richtext";
import { ReadMore } from "../ReadMore";

const Ctx = createContext({});

const TRANSCRIPT_URL = "https://speakapp.com/t/tools/instagram-transcript";
const inputCls = "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-bordeaux";
const areaCls = "w-full resize-y rounded-xl border border-line bg-white p-3 text-sm font-light leading-relaxed text-ink-soft outline-none transition-colors focus:border-bordeaux";
const copy = (t) => { navigator.clipboard?.writeText(htmlToText(t)); toast.success("Copié"); };

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[120px_1fr] sm:items-center sm:gap-4">
      <span className="text-sm text-ink-muted">{label}</span><div>{children}</div>
    </div>
  );
}

function Area({ label, k, testid, dark, heading, placeholder }) {
  const { local, set, addComment } = useContext(Ctx);
  return (
    <div>
      <p className={heading ? "text-sm font-semibold text-ink" : `text-[11px] font-medium uppercase tracking-[0.18em] ${dark ? "text-white/50" : "text-ink-muted"}`}>{label}</p>
      <div className="mt-2">
        <RichTextField testid={testid} fieldKey={k} initialHtml={local[k] || ""} onChange={set(k)} onComment={addComment} dark={dark}
          placeholder={placeholder || (dark ? `Colle ou écris le ${label.toLowerCase()}…` : "")}
          className={dark ? "min-h-[60px] rounded-2xl border border-white/10 bg-white/5 p-4 text-sm font-light text-white" : "min-h-[60px] rounded-xl border border-line bg-white p-3 text-sm font-light leading-relaxed text-ink-soft focus:border-bordeaux"} />
      </div>
    </div>
  );
}

function Prep() {
  const { local } = useContext(Ctx);
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="content-prep" className="rounded-[24px] border border-bordeaux-border bg-bordeaux-tint/40">
      <button data-testid="content-prep-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-5 py-4 text-left">
        <span className="font-medium text-ink">Préparation du post</span>
        <ChevronDown size={18} className={`text-bordeaux transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4 px-5 pb-5">
          <p className="text-sm font-semibold text-ink">Dans la tête de ma cible</p>
          <Area label="Symptômes" k="cible_symptomes" />
          <Area label="Ce qu'elle ressent" k="cible_ressent" />
          <Area label="Sa croyance actuelle" k="cible_croyance" />
          <Area heading testid="content-editor-message" label="Le message à faire passer" k="prise_conscience" />
          <Area heading testid="content-editor-idees" label="Mes idées" k="idees" placeholder="• Une idée par ligne…" />
          {local.notes && <Area label="Notes" k="notes" />}
        </div>
      )}
    </div>
  );
}

function HookList({ label, hint, list, onChange, addLabel, testid }) {
  const [ver, setVer] = useState(0);
  const items = list.length ? list : [""];
  const upd = (n, v) => onChange(items.map((x, i) => (i === n ? v : x)));
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3"><p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">{label}</p><span className="text-[11px] text-ink-muted">{hint}</span></div>
      <div className="mt-2 space-y-2">
        {items.map((h, n) => (
          <div key={`${ver}-${n}`} className="group flex items-start gap-2 rounded-2xl bg-white px-5 py-3 shadow-[0_2px_12px_rgba(45,24,30,0.05)]">
            <div className="min-w-0 flex-1"><RichTextField testid={`${testid}-${n}`} initialHtml={h} onChange={(v) => upd(n, v)} placeholder="Écris ton accroche…" className="min-h-[28px] bg-transparent py-1 text-sm text-ink" /></div>
            <button onClick={() => copy(h)} className="rounded-full p-1.5 text-ink-muted hover:bg-cream hover:text-bordeaux"><Copy size={13} /></button>
            <button onClick={() => { onChange(items.filter((_, i) => i !== n)); setVer((v) => v + 1); }} className="rounded-full p-1.5 text-ink-muted hover:bg-cream hover:text-bordeaux"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <button data-testid={`${testid}-add`} onClick={() => onChange([...items, ""])} className="mt-2 flex items-center gap-2 text-sm font-medium text-ink-soft hover:text-bordeaux"><span className="flex h-6 w-6 items-center justify-center rounded-full border border-line bg-white"><Plus size={12} /></span>{addLabel}</button>
    </div>
  );
}

function Mirror({ onClose }) {
  const { local, set } = useContext(Ctx);
  return (
    <div data-testid="content-mirror" className="rounded-[28px] p-5 text-white sm:p-6" style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 80% 0%, rgba(123,17,58,0.5), transparent 55%)" }}>
      <div className="flex items-center justify-between"><p className="text-[11px] font-semibold uppercase tracking-[0.2em]"><span className="text-[#F3D2D9]">Miroir</span> · Inspiration</p><button onClick={onClose} className="rounded-full p-1.5 text-white/60 hover:bg-white/10"><X size={15} /></button></div>
      <ol className="mt-4 space-y-1.5 rounded-2xl border border-white/10 bg-white/5 p-4 text-xs font-light text-white/75">
        <li>1. Va sur le contenu qui t'inspire et copie son lien.</li>
        <li>2. Colle le lien sur <a href={TRANSCRIPT_URL} target="_blank" rel="noreferrer" data-testid="mirror-transcript-link" className="inline-flex items-center gap-1 font-medium text-[#F3D2D9] underline">l'outil de transcription <ExternalLink size={11} /></a>.</li>
        <li>3. Colle ici le hook, le text hook et le script, puis réécris le tien à droite.</li>
      </ol>
      <div className="mt-4 space-y-4">
        <div className="relative"><Link2 size={14} className="absolute left-4 top-3.5 text-white/40" /><input data-testid="mirror-link" value={local.ref_url || ""} onChange={(e) => set("ref_url")(e.target.value)} placeholder="Lien du contenu d'origine" className="w-full rounded-2xl border border-white/10 bg-white/5 py-3 pl-10 pr-4 text-sm text-white outline-none placeholder:text-white/30" /></div>
        <Area dark testid="mirror-hook" label="Hook" k="mirror_hook" />
        <Area dark testid="mirror-text-hook" label="Text hook" k="mirror_text_hook" />
        <Area dark testid="mirror-script" label="Script" k="mirror_script" />
      </div>
    </div>
  );
}

function Comments({ isAdmin }) {
  const { local, removeComment } = useContext(Ctx);
  const list = local.comments || [];
  if (!list.length) return null;
  const viewer = isAdmin ? "admin" : "cliente";
  return (
    <div data-testid="content-comments" className="rounded-[24px] p-5 text-white shadow-[0_12px_40px_rgba(45,24,30,0.25)]" style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.55), transparent 60%)" }}>
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D2D9]"><MessageSquareText size={14} /> Commentaires</p>
      <div className="mt-3 space-y-3">
        {list.map((c) => (
          <div key={c.id} data-testid={`content-comment-${c.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-3">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-bordeaux text-[10px] font-semibold text-white ring-1 ring-white/20">{c.author === "admin" ? "F" : "C"}</span>
              <span className="text-xs font-semibold tracking-wide text-white">{c.author === "admin" ? "Fanny" : (isAdmin ? "Ta cliente" : "Moi")}</span>
              {c.author === viewer && <button data-testid={`content-comment-delete-${c.id}`} onClick={() => removeComment(c.id)} className="ml-auto text-white/50 hover:text-white"><Trash2 size={13} /></button>}
            </div>
            <ReadMore text={c.text} className="mt-1.5 text-sm font-light text-white/90" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CoachFeedback({ item, isAdmin, onSend }) {
  const [text, setText] = useState(item.coach_feedback || "");
  if (!isAdmin && !item.coach_feedback) return null;
  return (
    <div data-testid="content-coach-feedback" className="rounded-2xl bg-[#2D181E] p-5 text-white">
      <p className="flex items-center gap-2 text-sm font-medium"><MessageCircle size={16} /> Retour de ta coach</p>
      {isAdmin ? <>
        <textarea data-testid="content-feedback-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ton retour sur ce contenu…" className="mt-3 w-full resize-y rounded-xl border border-white/20 bg-white/10 p-3 text-sm font-light outline-none placeholder:text-white/50" />
        <button data-testid="content-feedback-send" onClick={() => onSend(text)} className="mt-3 flex items-center gap-2 rounded-full bg-bordeaux px-4 py-2 text-xs font-medium"><Send size={13} /> Envoyer mon retour</button>
      </> : <p className="mt-3 whitespace-pre-wrap text-sm font-light text-white/90">{item.coach_feedback}</p>}
    </div>
  );
}

export default function ContentEditor({ item, isAdmin, actions, onClose }) {
  const [local, setLocal] = useState(() => ({ ...item, hooks: item.hooks?.length ? item.hooks : item.hook ? [item.hook] : [], text_hooks: item.text_hooks || [] }));
  const [full, setFull] = useState(false);
  const [mirror, setMirror] = useState(!!(item.mirror_script || item.mirror_hook));
  const [saved, setSaved] = useState(true);
  const pending = useRef({});
  const timer = useRef(null);

  const flush = () => {
    clearTimeout(timer.current);
    if (Object.keys(pending.current).length) { actions.updateItem(item.id, pending.current); pending.current = {}; }
    setSaved(true);
  };
  useEffect(() => () => flush(), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { flush(); onClose(); } };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("beforeunload", flush); document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const queue = (patch) => {
    setLocal((l) => ({ ...l, ...patch }));
    pending.current = { ...pending.current, ...patch };
    setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };
  const set = (k) => (v) => queue({ [k]: v });
  const setNow = (patch) => { queue(patch); flush(); };
  const setHooks = (hooks) => queue({ hooks, hook: hooks.find((h) => htmlToText(h).trim()) || "" });
  const close = () => { flush(); onClose(); };
  const setMetric = (k, v) => set("metrics")({ ...local.metrics, [k]: Math.max(0, parseInt(v, 10) || 0) });
  const wide = full || mirror;
  const words = htmlToText(local.post).trim().split(/\s+/).filter(Boolean).length;
  const addComment = ({ id, key, quote, text }) => setNow({ comments: [...(local.comments || []), { id, key, quote, text, author: isAdmin ? "admin" : "cliente", created_at: new Date().toISOString() }] });
  const removeComment = (id) => {
    const span = document.querySelector(`[data-testid=content-editor] [data-cid="${id}"]`);
    const patch = { comments: (local.comments || []).filter((c) => c.id !== id) };
    if (span) {
      const field = span.closest("[contenteditable]");
      const p = span.parentNode;
      while (span.firstChild) p.insertBefore(span.firstChild, span);
      p.removeChild(span);
      if (field?.dataset.field) patch[field.dataset.field] = field.innerHTML;
    }
    setNow(patch);
  };

  const writing = (
    <div className="space-y-6">
      <HookList testid="content-hooks" label="Hook" hint="Pour tester plusieurs accroches" list={local.hooks} onChange={setHooks} addLabel="Ajouter un autre hook" />
      <HookList testid="content-text-hooks" label="Text hook" hint="Pour tester plusieurs accroches textuelles" list={local.text_hooks} onChange={set("text_hooks")} addLabel="Ajouter un autre text hook" />
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">Script</p>
        <div className="relative mt-2 rounded-2xl bg-white p-5 shadow-[0_2px_12px_rgba(45,24,30,0.05)]">
          <button onClick={() => copy(local.post)} className="absolute right-3 top-3 rounded-full p-1.5 text-ink-muted hover:bg-cream hover:text-bordeaux"><Copy size={13} /></button>
          <RichTextField testid="content-editor-script" fieldKey="post" initialHtml={local.post || ""} onChange={set("post")} onComment={addComment} placeholder="Écris ton script ici…" className="min-h-[260px] bg-transparent pr-8 text-sm leading-relaxed text-ink" />
          <p className="mt-2 text-[11px] font-medium uppercase tracking-wider text-ink-muted">{words} mots</p>
        </div>
      </div>
      <Area testid="content-editor-description" label="Description" k="description" />
      <Comments isAdmin={isAdmin} />
    </div>
  );

  return createPortal(
    <Ctx.Provider value={{ local, set, addComment, removeComment }}>
    <div className="fixed inset-0 z-[100] bg-ink/30 backdrop-blur-[2px]" onClick={close}>
      <div data-testid="content-editor" onClick={(e) => e.stopPropagation()} className={wide ? "fixed inset-0 overflow-y-auto bg-cream" : "fixed bottom-0 right-0 top-0 w-full max-w-2xl overflow-y-auto border-l border-line bg-cream shadow-2xl"}>
        <div className={wide ? "mx-auto max-w-7xl p-5 sm:p-8" : "p-5 sm:p-8"}>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button onClick={() => { if (window.confirm("Supprimer définitivement ce contenu ?")) { clearTimeout(timer.current); pending.current = {}; actions.deleteItem(item.id); onClose(); } }} data-testid="content-editor-delete" className="flex items-center gap-1.5 px-2 text-sm text-ink-muted hover:text-bordeaux"><Trash2 size={15} /> Supprimer</button>
            </div>
            <div className="flex items-center gap-2">
              <span data-testid="content-editor-saved" className="hidden text-[11px] uppercase tracking-wider text-ink-muted sm:inline">{saved ? "● Enregistré automatiquement" : "Enregistrement…"}</span>
              {!mirror && <button onClick={() => setFull(!full)} data-testid="content-editor-fullscreen" title={full ? "Réduire" : "Plein écran"} className="rounded-full p-2 text-ink-muted hover:bg-bordeaux-tint hover:text-bordeaux">{full ? <Minimize2 size={17} /> : <Maximize2 size={17} />}</button>}
              <button onClick={close} data-testid="content-editor-close" className="rounded-full p-2 text-ink-muted hover:bg-bordeaux-tint hover:text-bordeaux"><X size={18} /></button>
            </div>
          </div>

          <input data-testid="content-editor-title" value={local.title || ""} onChange={(e) => set("title")(e.target.value)} placeholder="Titre du contenu…" className="w-full border-0 bg-transparent font-sans text-2xl font-medium text-ink outline-none placeholder:text-ink-muted/50" />
          {item.source === "coach" && <p className="mt-1 text-xs font-medium uppercase tracking-wider text-bordeaux">Idée proposée par ta coach</p>}

          <div className="mt-5 divide-y divide-line/70 rounded-2xl border border-line bg-white px-4">
            <Row label="Statut">
              <div className="flex flex-wrap gap-1.5">{STATUSES.map((s) => <button key={s.key} data-testid={`content-status-${s.key}`} onClick={() => setNow({ status: s.key })} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${local.status === s.key ? s.cls : "border border-line text-ink-muted hover:text-bordeaux"}`}>{s.label}</button>)}</div>
            </Row>
            <Row label="Date">
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" data-testid="content-editor-date" value={local.date || ""} onChange={(e) => setNow({ date: e.target.value || null, ...(local.status === "idee" && e.target.value ? { status: "planifie" } : {}) })} className={inputCls + " max-w-[200px]"} />
                {local.date && <button data-testid="content-editor-unplan" onClick={() => setNow({ date: null, status: "idee" })} className="flex items-center gap-1 text-xs text-ink-muted hover:text-bordeaux"><CalendarX size={14} /> Retirer du calendrier</button>}
              </div>
            </Row>
            <Row label="Format"><select data-testid="content-editor-format" value={local.format || ""} onChange={(e) => setNow({ format: e.target.value })} className={inputCls}><option value="">—</option>{FORMATS.map((f) => <option key={f}>{f}</option>)}</select></Row>
            <Row label="Objectif"><select data-testid="content-editor-objectif" value={local.objectif || ""} onChange={(e) => setNow({ objectif: e.target.value })} className={inputCls}><option value="">—</option>{OBJECTIFS.map((f) => <option key={f}>{f}</option>)}</select></Row>
            <Row label="Lien du contenu"><input data-testid="content-editor-link" value={local.link || ""} onChange={(e) => set("link")(e.target.value)} placeholder="Lien Instagram une fois publié (la couverture s'affiche automatiquement)" className={inputCls} /></Row>
          </div>

          <div className="mt-6"><Prep /></div>

          <button data-testid="content-mirror-toggle" onClick={() => setMirror(!mirror)} className={`mt-4 flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-6 text-sm font-medium shadow-[0_8px_24px_rgba(123,17,58,0.35)] transition-transform hover:-translate-y-0.5 ${mirror ? "bg-[#2D181E] text-white" : "bg-bordeaux text-white"}`}><span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15"><Columns2 size={15} /></span> {mirror ? "Fermer le miroir" : "Mettre en miroir"} {mirror && <span className="h-1.5 w-1.5 rounded-full bg-[#F3D2D9]" />}</button>

          <div className={`mt-6 ${mirror ? "grid grid-cols-1 items-start gap-6 lg:grid-cols-2 [&>*]:min-w-0" : ""}`}>
            {mirror && <div className="lg:sticky lg:top-4"><Mirror onClose={() => setMirror(false)} /></div>}
            {writing}
          </div>

          {local.status === "publie" && (
            <div data-testid="content-editor-metrics" className="mt-8 rounded-[28px] p-6 text-white shadow-[0_12px_40px_rgba(45,24,30,0.25)]" style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.6), transparent 55%)" }}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-bordeaux"><BarChart3 size={18} /></span><div><p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D2D9]">Performance</p>{local.date && <p className="text-xs text-white/50">Publié le {fmtDay(local.date)}</p>}</div></div>
                <div className="rounded-2xl bg-bordeaux px-5 py-2 text-right"><p className="text-[10px] uppercase tracking-wider text-white/70">Engagement</p><p className="font-sans text-2xl font-medium">{fmtPct(engagement(local.metrics))}</p></div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {METRICS.map((m) => <label key={m.key} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-[11px] text-white/60">{m.label}<input type="number" min="0" data-testid={`content-editor-metric-${m.key}`} value={local.metrics?.[m.key] || 0} onChange={(e) => setMetric(m.key, e.target.value)} className="mt-1 w-full bg-transparent font-sans text-2xl font-medium text-white outline-none" /></label>)}
              </div>
            </div>
          )}

          <div className="mt-6"><CoachFeedback item={{ ...item, ...local }} isAdmin={isAdmin} onSend={async (t) => { const d = await actions.itemFeedback(item.id, t); if (d) setLocal((l) => ({ ...l, coach_feedback: d.coach_feedback })); }} /></div>
        </div>
      </div>
    </div>
    </Ctx.Provider>,
    document.body
  );
}
